import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, tenantAPI } from '../lib/api';

const AuthContext = createContext(null);

// Capability constants (mirror backend)
export const Capability = {
  CREATE_CLIENT: 'create_client',
  CREATE_OBRA: 'create_obra',
  CREATE_PRESUPUESTO: 'create_presupuesto',
  CREATE_FACTURA: 'create_factura',
  CREATE_GASTO: 'create_gasto',
  CREATE_PAGO: 'create_pago',
  CREATE_INCIDENCIA: 'create_incidencia',
  CREATE_PENDIENTE: 'create_pendiente',
  EDIT_CLIENT: 'edit_client',
  EDIT_OBRA: 'edit_obra',
  EDIT_PRESUPUESTO: 'edit_presupuesto',
  EDIT_FACTURA: 'edit_factura',
  EDIT_GASTO: 'edit_gasto',
  DELETE_DATA: 'delete_data',
  EXPORT_PDF: 'export_pdf',
  EXPORT_EXCEL: 'export_excel',
  MANAGE_USERS: 'manage_users',
  MANAGE_COMPANY: 'manage_company',
  INVITE_USERS: 'invite_users',
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_DATA: 'view_data',
};

// Role constants
export const UserRole = {
  ADMIN: 'admin',
  USER: 'user',
  READONLY: 'readonly',
};

// Plan constants
export const TenantPlan = {
  TRIAL: 'trial',
  ACTIVE: 'active',
  TRIAL_EXPIRED: 'trial_expired',
  BLOCKED: 'blocked',
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [capabilities, setCapabilities] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchCapabilities = useCallback(async () => {
    try {
      const response = await tenantAPI.getCapabilities();
      setCapabilities(response.data.capabilities || {});
      return response.data;
    } catch (error) {
      console.error('Error fetching capabilities:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedTenant = localStorage.getItem('tenant');
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      if (storedTenant) {
        setTenant(JSON.parse(storedTenant));
      }
      // Verify token and refresh info
      authAPI.me()
        .then(res => {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
          if (res.data.tenant) {
            setTenant(res.data.tenant);
            localStorage.setItem('tenant', JSON.stringify(res.data.tenant));
          }
          // Fetch capabilities
          fetchCapabilities();
        })
        .catch(() => {
          logout();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [fetchCapabilities]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData, tenant: tenantData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (tenantData) {
      localStorage.setItem('tenant', JSON.stringify(tenantData));
      setTenant(tenantData);
    }
    setUser(userData);
    // Fetch capabilities after login
    await fetchCapabilities();
    return { user: userData, tenant: tenantData };
  };

  const register = async (email, password, nombre) => {
    const response = await authAPI.register({ email, password, nombre });
    const { access_token, user: userData, tenant: tenantData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (tenantData) {
      localStorage.setItem('tenant', JSON.stringify(tenantData));
      setTenant(tenantData);
    }
    setUser(userData);
    await fetchCapabilities();
    return { user: userData, tenant: tenantData };
  };

  const registerCompany = async (data) => {
    const response = await authAPI.registerCompany(data);
    const { access_token, user: userData, tenant: tenantData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (tenantData) {
      localStorage.setItem('tenant', JSON.stringify(tenantData));
      setTenant(tenantData);
    }
    setUser(userData);
    await fetchCapabilities();
    return { user: userData, tenant: tenantData, message: response.data.message };
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    setUser(null);
    setTenant(null);
    setCapabilities({});
  };

  const updateTenant = (tenantData) => {
    setTenant(tenantData);
    localStorage.setItem('tenant', JSON.stringify(tenantData));
  };

  // Check if user has a specific capability
  const can = (capability) => {
    // Superadmins can do everything
    if (user?.is_superadmin) return true;
    return capabilities[capability] === true;
  };

  // Trial status helpers
  const trialStatus = tenant?.trial_status || {};
  const isTrialExpired = trialStatus.status === TenantPlan.TRIAL_EXPIRED;
  const isBlocked = trialStatus.status === TenantPlan.BLOCKED;
  const isInTrial = trialStatus.status === TenantPlan.TRIAL;
  const isActive = trialStatus.status === TenantPlan.ACTIVE || tenant?.is_demo;
  const daysRemaining = trialStatus.days_remaining;

  // Computed values
  const companyName = tenant?.name || 'Mi Empresa';
  const isDemo = tenant?.is_demo || false;
  const isSuperadmin = user?.is_superadmin || false;
  const userRole = user?.role || UserRole.READONLY;

  return (
    <AuthContext.Provider value={{ 
      user, 
      tenant,
      companyName,
      isDemo,
      isSuperadmin,
      userRole,
      capabilities,
      can,
      // Trial status
      trialStatus,
      isTrialExpired,
      isBlocked,
      isInTrial,
      isActive,
      daysRemaining,
      // Actions
      loading, 
      login, 
      register, 
      registerCompany,
      logout,
      updateTenant,
      fetchCapabilities,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
